import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { map, of, switchMap } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DevicesService, MeasurementsService, StagesService } from '@cosmos/angular/api';
import { ErrorService, MeasurementRefreshService, RBACService, StateService } from '@cosmos/angular/utils';
import { Device, Measurement, MEASUREMENT_TYPE_DISPLAY_CONFIG, Project, User } from '@cosmos/shared';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MessageModule } from 'primeng/message';
import { DynamicLineChartComponent } from '../../data/charts/dynamic-line-chart/dynamic-line-chart.component';
import { EditMeasurementForm, MeasurementChangeDeviceForm } from '../../dynamic-form/forms/measurement.form';
import { DynamicFormDialog } from '../../dynamic-form/dialog-form/dialog-form.component';
import { MeasurementChangeStageDialogComponent } from '../../components/measurement-change-stage-dialog/measurement-change-stage-dialog.component';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { MeasurementLivenessBadgeComponent } from '../../components/measurement-liveness-badge/measurement-liveness-badge.component';
import { BreadcumbsService } from '../../navigation/breadcrumbs/breadcrumbs.service';
import { MonitoringTreeService } from '../../monitoring/monitoring-tree.service';
import { MeasurementInfoCardComponent } from '../../components/measurement-info-card/measurement-info-card.component';

@Component({
  selector: 'cosmos-measurement-awaiting-start',
  templateUrl: './measurement-awaiting-start.component.html',
  imports: [
    TranslatePipe, DatePipe, FormsModule, CardModule, ButtonModule, SelectModule, MessageModule,
    DynamicDialogModule, ConfirmDialogModule, DynamicLineChartComponent,
    MeasurementLivenessBadgeComponent, MeasurementInfoCardComponent,
  ],
  providers: [DialogService, ConfirmationService, TranslateService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasurementAwaitingStartComponent {
  private readonly measurementsService = inject(MeasurementsService);
  private readonly devicesService = inject(DevicesService);
  private readonly stagesService = inject(StagesService);
  private readonly errorService = inject(ErrorService);
  private readonly translateService = inject(TranslateService);
  private readonly dialogService = inject(DialogService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly stateService = inject(StateService);
  private readonly refreshService = inject(MeasurementRefreshService);
  private readonly breadcumbsService = inject(BreadcumbsService);
  private readonly treeService = inject(MonitoringTreeService);
  readonly rbac = inject(RBACService);

  readonly measurementId = input<string>();

  readonly measurement = signal<Measurement | null>(null);
  readonly device = signal<Device | null>(null);
  readonly project = signal<Project | null>(null);

  user: User;

  readonly displayConfig = computed(() => {
    const m = this.measurement();
    if (!m?.measurementType) return null;
    return MEASUREMENT_TYPE_DISPLAY_CONFIG[m.measurementType] ?? null;
  });

  readonly pointCount = computed(() => this.measurement()?.timeAll?.length ?? 0);
  readonly hasData = computed(() => this.pointCount() > 0);
  readonly firstTimestamp = computed(() => this.measurement()?.timeAll?.[0] ?? null);
  readonly lastTimestamp = computed(() => {
    const t = this.measurement()?.timeAll;
    return t?.length ? t[t.length - 1] : null;
  });

  // Bound directly to cosmos-dynamic-line-chart's `dataObject` input, which
  // expects an object keyed by dataset name plus a `time` array (see
  // MaturityMeasurementComponent's usage of the same chart component).
  readonly chartDataObject = computed(() => {
    const m = this.measurement();
    const config = this.displayConfig();
    if (!m || !config) return null;
    return {
      _id: m._id,
      time: m.timeAll ?? [],
      temperature: m.temperatureAll ?? [],
    };
  });

  readonly chartDatasets = computed(() => this.displayConfig()?.rawVariables ?? []);

  readonly startTimestampOptions = computed(() => {
    const timeAll = this.measurement()?.timeAll ?? [];
    return timeAll.map((t, index) => ({ value: index, label: new Date(t).toLocaleString() }));
  });

  selectedStartTimestamp = signal<number | null>(null);

  constructor() {
    effect(() => {
      if (this.measurementId()) {
        this.loadMeasurement();
      }
    });

    // Default the picker to the latest buffered point once options are known.
    effect(() => {
      const options = this.startTimestampOptions();
      if (options.length && this.selectedStartTimestamp() === null) {
        this.selectedStartTimestamp.set(options[options.length - 1].value);
      }
    });

    this.stateService.currentUser$.subscribe((x) => { this.user = x; });
  }

  loadMeasurement(): void {
    this.measurementsService.findById(this.measurementId(), '?populate_project&populate_device=name,deviceProfile').subscribe({
      next: (response) => {
        this.project.set(response.body.project as Project);
        this.measurement.set(response.body);
      },
      error: (e) => this.errorService.handleError(e),
      complete: () => {
        this.device.set(this.measurement().device as Device || null);
      },
    });
  }

  startMeasurement(): void {
    // With no data yet, there's nothing to pick from — start at index 0 so
    // the measurement is marked started and computes from the first point
    // ingested from here on (backend allows timestampStart=0 when timeAll is empty).
    const timestampStart = this.hasData() ? this.selectedStartTimestamp() : 0;
    if (timestampStart === null) return;

    this.measurementsService.start(this.measurementId(), timestampStart).subscribe(
      this.errorService.handleMutation({
        successMessageKey: 'measurement.start.success',
        onSuccess: () => this.refreshService.trigger(),
      }),
    );
  }

  editMeasurement(): void {
    const m = this.measurement();
    const editMeasurementForm = new EditMeasurementForm(this.translateService, m, this.user, {
      visualizationConfig: this.project()?.visualizationConfig, measurementType: m.measurementType,
    });
    const dialogRef = this.dialogService.open(DynamicFormDialog, {
      data: { form: editMeasurementForm },
      modal: true, closable: true,
      header: this.translateService.instant('dialog.title.edit.measurement'),
      width: '50vw',
      breakpoints: { '960px': '75vw', '640px': '90vw' },
    });

    dialogRef.onClose.subscribe((result) => {
      if (result !== undefined) {
        this.measurementsService.update([m._id, result]).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.updateSuccess',
            onSuccess: () => this.loadMeasurement(),
          }),
        );
      }
    });
  }

  changeDevice(): void {
    const m = this.measurement();
    const device = this.device();
    const projectId = this.project()?._id;
    this.devicesService.find(`/?project=${projectId}&available=true&deviceType=DataAcquisition&deviceProfile=${device?.deviceProfile}`).subscribe({
      next: (response) => {
        const devices = response.body;
        if (devices.length > 0) {
          const measurementChangeDeviceForm = new MeasurementChangeDeviceForm(this.translateService, devices, this.user, {});
          const dialogRef = this.dialogService.open(DynamicFormDialog, {
            data: { form: measurementChangeDeviceForm },
            header: this.translateService.instant('measurement.change_device'),
            modal: true, closable: true, width: '50vw',
            breakpoints: { '960px': '75vw', '640px': '90vw' },
          });
          dialogRef.onClose.subscribe((result) => {
            if (result !== undefined) {
              this.measurementsService.changeDevice(m._id, result).subscribe(
                this.errorService.handleMutation({
                  successMessageKey: 'measurement.messages.updateSuccess',
                  onSuccess: () => this.loadMeasurement(),
                }),
              );
            }
          });
        } else {
          this.errorService.notify('warn', this.translateService.instant('devices.not-found'));
        }
      },
      error: (e) => {
        if (e.error?.message === 'Devices not Found') {
          this.errorService.notify('warn', this.translateService.instant('devices.not-found'));
        }
      },
    });
  }

  moveStage(): void {
    const m = this.measurement();
    const projectId = this.project()?._id;
    this.fetchProjectStageTree(projectId).subscribe({
      next: (stageTree) => {
        const dialogRef = this.dialogService.open(MeasurementChangeStageDialogComponent, {
          data: { measurement: m, stageTree },
          header: this.translateService.instant('measurement.move_stage.title'),
          modal: true, closable: true, width: '30vw',
          breakpoints: { '960px': '75vw', '640px': '90vw' },
        });
        dialogRef.onClose.subscribe((result: { stage: string } | undefined) => {
          if (result !== undefined) {
            this.measurementsService.changeStage(m._id, result).subscribe(
              this.errorService.handleMutation({
                successMessageKey: 'measurement.messages.updateSuccess',
                onSuccess: () => {
                  // The measurement's URL no longer encodes its stage (flat
                  // /monitoring/measurement/:id route), so a stage-only move
                  // doesn't change the URL — no navigate needed.
                  this.refreshService.trigger();
                  // The measurement moved to a different stage — the tree rail
                  // must re-parent its leaf.
                  this.treeService.refresh();
                  // Breadcrumbs are only re-resolved on real navigation; a
                  // stage-only move doesn't change the URL, so nothing else
                  // would refresh the (now stale) breadcrumb trail.
                  this.breadcumbsService.refresh();
                  // Keeps this component's own `measurement` signal in sync,
                  // so re-opening the dialog shows the new stage, not the old one.
                  this.loadMeasurement();
                },
              }),
            );
          }
        });
      },
      error: (e) => this.errorService.handleError(e),
    });
  }

  // Fetches every root stage in the project, then each root's full
  // descendant tree — the shape the change-stage dialog's tree picker needs.
  private fetchProjectStageTree(projectId: string) {
    return this.stagesService.find(`?project=${projectId}&rootOnly=true`).pipe(
      switchMap((rootsRes) => {
        const roots = (rootsRes.body as any[]) ?? [];
        if (roots.length === 0) return of([]);
        return this.stagesService.getForest(roots.map((r) => r._id)).pipe(map((res) => res.body ?? []));
      }),
    );
  }

  finishMeasurement(event: Event): void {
    this.confirmationService.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translateService.instant('measurement.finish-dialog.message'),
      icon: 'fa-solid fa-warning',
      header: this.translateService.instant('dialog.confirm.warning'),
      closable: true,
      closeOnEscape: true,
      rejectButtonProps: {
        label: this.translateService.instant('button.cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.translateService.instant('button.finish'),
      },
      accept: () => {
        this.measurementsService.finish(this.measurementId()).subscribe(
          this.errorService.handleMutation({
            successMessageKey: 'measurement.messages.finishSuccess',
            onSuccess: () => this.refreshService.trigger(),
          }),
        );
      },
      reject: () => null,
    });
  }
}
